import { useEffect, useMemo, useState, type MouseEvent } from 'react';
import { Link } from 'react-router-dom';
import type { InvoiceBulkPatch, InvoiceCollectionRecord, InvoiceStatus } from '@shared/types';
import { subscribeToShellDeleteRequests } from '@renderer/app/events/shell-delete-events';
import { subscribeToShellDomainInvalidations } from '@renderer/app/events/shell-domain-events';
import { InlineQuickAddPanel, createQuickAddContext } from '@renderer/app/quick-add/quick-add-shared';
import { useShellStore } from '@renderer/app/state/shell-store';
import { BulkEditorRail } from '@renderer/shared/components/bulk-editor-rail';
import {
  CollectionColumnSettingsModal,
  resolveVisibleCollectionColumns,
  type CollectionColumnConfig
} from '@renderer/shared/components/collection-column-settings';
import {
  CardGridSurface,
  CollectionSelectionToggle,
  CollectionToolbar,
  CollectionViewToggle,
  DataTableSurface,
  EmptyCollectionState
} from '@renderer/shared/components/collection-scaffold';
import { CollectionTable, type CollectionSortState } from '@renderer/shared/components/collection-table';
import { SplitWorkspaceLayout } from '@renderer/shared/components/split-workspace';
import {
  FilterChip,
  IconButton,
  OverflowMenuButton,
  PanelCard,
  SectionHeader,
  SelectField,
  StatusBadge,
  TextField,
  ToolbarGroup
} from '@renderer/shared/components/primitives';
import { useI18n } from '@renderer/shared/i18n';
import { applyBulkInvoicePatch, deleteInvoices, listInvoices } from './invoices-repository';
import {
  selectInvoiceCustomerOptions,
  selectInvoiceIssuerOptions,
  selectInvoiceRows,
  selectInvoiceSelectionItems,
  selectInvoicesSummary,
  type InvoiceTableRowViewModel
} from './invoices-selectors';
import { useInvoicesViewStore, type InvoicesSort } from './invoices-view-store';

type PatchMode = 'unchanged' | 'clear' | 'set';
type SortDirection = 'asc' | 'desc';
type InvoiceBulkDraft = Omit<InvoiceBulkPatch, 'invoiceIds'>;

const invoiceStatuses: InvoiceStatus[] = ['draft', 'issued', 'sent', 'paid', 'partially_paid', 'overdue', 'cancelled', 'archived'];

function createInitialInvoiceBulkDraft(): InvoiceBulkDraft {
  return {
    status: { mode: 'unchanged' },
    projectId: { mode: 'unchanged' },
    dueDate: { mode: 'unchanged' },
    paidAt: { mode: 'unchanged' },
    paymentNotes: { mode: 'unchanged' }
  };
}

function formatDateInputValue(value: string | null): string {
  return value ? value.slice(0, 10) : '';
}

function toIsoDate(value: string): string {
  return `${value}T12:00:00.000Z`;
}

function invoiceSortToColumnId(sort: InvoicesSort): string {
  switch (sort) {
    case 'dueDate':
      return 'dueDate';
    case 'number':
      return 'reference';
    default:
      return 'issueDate';
  }
}

function invoiceColumnIdToSort(columnId: string): InvoicesSort | null {
  switch (columnId) {
    case 'dueDate':
      return 'dueDate';
    case 'reference':
      return 'number';
    case 'issueDate':
      return 'issueDate';
    default:
      return null;
  }
}

function getDefaultInvoiceSortDirection(sort: InvoicesSort): SortDirection {
  return sort === 'number' ? 'asc' : 'desc';
}

export function InvoicesScreen(): JSX.Element {
  const { locale, t } = useI18n();
  const preferences = useShellStore((state) => state.preferences);
  const savePreferences = useShellStore((state) => state.savePreferences);
  const [collection, setCollection] = useState<InvoiceCollectionRecord | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [mutationError, setMutationError] = useState<string | null>(null);
  const [reloadToken, setReloadToken] = useState(0);
  const [invoiceBulkDraft, setInvoiceBulkDraft] = useState<InvoiceBulkDraft>(createInitialInvoiceBulkDraft);
  const [columnSettingsOpen, setColumnSettingsOpen] = useState(false);
  const {
    statusFilter,
    customerFilter,
    issuerFilter,
    dueFilter,
    sort,
    selectionSession,
    setStatusFilter,
    setCustomerFilter,
    setIssuerFilter,
    setDueFilter,
    setSort,
    setViewMode,
    syncSelectionQuery,
    toggleSelection,
    removeSelectedId,
    clearSelection
  } = useInvoicesViewStore();
  const [sortDirection, setSortDirection] = useState<SortDirection>(getDefaultInvoiceSortDirection(sort));

  useEffect(() => {
    let active = true;

    setError(null);
    void listInvoices()
      .then((nextCollection) => {
        if (active) {
          setCollection(nextCollection);
        }
      })
      .catch((nextError) => {
        if (active) {
          setError(nextError instanceof Error ? nextError.message : 'Invoices unavailable.');
        }
      });

    return () => {
      active = false;
    };
  }, [reloadToken]);

  useEffect(() => {
    return () => {
      clearSelection();
      setInvoiceBulkDraft(createInitialInvoiceBulkDraft());
    };
  }, [clearSelection]);

  useEffect(
    () =>
      subscribeToShellDomainInvalidations((detail) => {
        if (detail.domain === 'invoices') {
          setReloadToken((value) => value + 1);
        }
      }),
    []
  );

  const summary = useMemo(() => (collection ? selectInvoicesSummary(collection) : null), [collection]);
  const rows = useMemo(() => {
    if (!collection) {
      return [];
    }

    return selectInvoiceRows(collection, {
      locale,
      statusFilter,
      customerFilter,
      issuerFilter,
      dueFilter,
      sort,
      sortDirection,
      t
    });
  }, [collection, customerFilter, dueFilter, issuerFilter, locale, sort, sortDirection, statusFilter, t]);
  const invoiceColumns = useMemo<CollectionColumnConfig[]>(
    () => [
      { id: 'selection', label: '', className: 'av-collection-table__selection-cell', locked: true },
      { id: 'reference', label: t('invoices.table.reference'), sortable: true, locked: true },
      { id: 'customer', label: t('invoices.table.customer') },
      { id: 'issuer', label: t('invoices.table.issuer') },
      { id: 'issueDate', label: t('invoices.sort.issueDate'), sortable: true },
      { id: 'dueDate', label: t('invoices.sort.dueDate'), sortable: true },
      { id: 'amount', label: t('invoices.table.amount') },
      { id: 'actions', label: '', align: 'right', locked: true }
    ],
    [t]
  );
  const visibleInvoiceColumns = useMemo(
    () => resolveVisibleCollectionColumns(invoiceColumns, preferences.collectionColumnVisibility.invoices),
    [invoiceColumns, preferences.collectionColumnVisibility.invoices]
  );
  const selectionItems = useMemo(() => (collection ? selectInvoiceSelectionItems(collection) : []), [collection]);
  const visibleIds = useMemo(() => rows.map((row) => row.id), [rows]);
  const visibleIdsKey = visibleIds.join('|');
  const queryKey = `${statusFilter}|${customerFilter}|${issuerFilter}|${dueFilter}|${sort}|${sortDirection}|${preferences.invoicesViewMode}`;

  useEffect(() => {
    syncSelectionQuery(queryKey, visibleIds);
  }, [queryKey, syncSelectionQuery, visibleIds, visibleIdsKey]);

  async function handleDeleteSelected(invoiceIds: string[]): Promise<void> {
    if (invoiceIds.length === 0) {
      return;
    }

    setMutationError(null);

    try {
      const nextCollection = await deleteInvoices({ ids: invoiceIds });
      setCollection(nextCollection);
      clearSelection();
      setInvoiceBulkDraft(createInitialInvoiceBulkDraft());
    } catch (nextError) {
      setMutationError(nextError instanceof Error ? nextError.message : 'Invoice delete failed.');
    }
  }

  useEffect(
    () =>
      subscribeToShellDeleteRequests((detail) => {
        if (detail.domain !== 'invoices' || detail.ids.length === 0) {
          return;
        }

        void handleDeleteSelected(detail.ids);
      }),
    [clearSelection]
  );

  if (error) {
    return (
      <div className="av-page">
        <EmptyCollectionState title={t('invoices.title')} description={error} />
      </div>
    );
  }

  if (!collection || !summary) {
    return <div className="av-page" />;
  }

  const selectedItems = selectionSession.selectedIds
    .map((selectedId) => selectionItems.find((item) => item.id === selectedId) ?? null)
    .filter((item): item is NonNullable<typeof item> => item !== null);
  const projectOptions = collection.projectSummaries.map((project) => ({
    value: project.id,
    label: project.name
  }));
  const quickAddContext = createQuickAddContext({
    customers: collection.customerSummaries,
    projects: collection.projectSummaries,
    t
  });
  const hasInvoiceBulkDraftChanges = Object.values(invoiceBulkDraft).some((patch) => patch.mode !== 'unchanged');
  const isInvoiceBulkDraftValid =
    (invoiceBulkDraft.projectId.mode !== 'set' || invoiceBulkDraft.projectId.value.length > 0) &&
    (invoiceBulkDraft.dueDate.mode !== 'set' || invoiceBulkDraft.dueDate.value.length > 0) &&
    (invoiceBulkDraft.paidAt.mode !== 'set' || invoiceBulkDraft.paidAt.value.length > 0) &&
    (invoiceBulkDraft.paymentNotes.mode !== 'set' || invoiceBulkDraft.paymentNotes.value.trim().length > 0);
  const tableSortState: CollectionSortState = {
    columnId: invoiceSortToColumnId(sort),
    direction: sortDirection
  };

  function handleToggleSelection(invoiceId: string, event: MouseEvent<HTMLButtonElement>): void {
    event.preventDefault();
    event.stopPropagation();
    toggleSelection(invoiceId, visibleIds, queryKey, {
      additive: true,
      range: event.shiftKey
    });
  }

  function updateInvoiceBulkDraft(patch: Partial<InvoiceBulkDraft>): void {
    setInvoiceBulkDraft((current) => ({
      ...current,
      ...patch
    }));
  }

  async function handleApplyBulkChanges(): Promise<void> {
    if (selectedItems.length === 0 || !hasInvoiceBulkDraftChanges || !isInvoiceBulkDraftValid) {
      return;
    }

    setMutationError(null);

    try {
      const nextCollection = await applyBulkInvoicePatch({
        invoiceIds: selectionSession.selectedIds,
        ...invoiceBulkDraft
      });

      setCollection(nextCollection);
      clearSelection();
      setInvoiceBulkDraft(createInitialInvoiceBulkDraft());
    } catch (nextError) {
      setMutationError(nextError instanceof Error ? nextError.message : 'Invoice update failed.');
    }
  }

  function handleTableSortChange(nextSortState: CollectionSortState): void {
    const nextSort = invoiceColumnIdToSort(nextSortState.columnId);

    if (!nextSort) {
      return;
    }

    setSort(nextSort);
    setSortDirection(nextSortState.direction);
  }

  function handleSortSelectChange(nextSort: InvoicesSort): void {
    setSort(nextSort);
    setSortDirection(getDefaultInvoiceSortDirection(nextSort));
  }

  function handleVisibleColumnIdsChange(columnIds: string[]): void {
    void savePreferences({
      collectionColumnVisibility: {
        ...preferences.collectionColumnVisibility,
        invoices: columnIds
      }
    });
  }

  function handleResetVisibleColumns(): void {
    const nextVisibility = { ...preferences.collectionColumnVisibility };
    delete nextVisibility.invoices;
    void savePreferences({ collectionColumnVisibility: nextVisibility });
  }

  function handleNullablePatchModeChange(
    mode: PatchMode,
    fallbackValue: string,
    field: 'projectId' | 'dueDate' | 'paidAt' | 'paymentNotes'
  ): void {
    if (mode === 'set') {
      updateInvoiceBulkDraft({ [field]: { mode: 'set', value: fallbackValue } });
      return;
    }

    updateInvoiceBulkDraft({ [field]: { mode } });
  }

  function renderInvoiceCell(row: InvoiceTableRowViewModel, columnId: string): JSX.Element {
    const selected = selectionSession.selectedIds.includes(row.id);

    switch (columnId) {
      case 'selection':
        return (
          <td key={columnId} className="av-collection-table__cell av-collection-table__selection-cell">
            <CollectionSelectionToggle
              selected={selected}
              icon="invoices"
              label={row.reference}
              onClick={(event) => handleToggleSelection(row.id, event)}
            />
          </td>
        );
      case 'reference':
        return (
          <td key={columnId} className="av-collection-table__cell">
            <div className="av-collection-table__identity-copy">
              <strong>
                <Link className="av-inline-link" to={`/invoices/${row.id}`}>
                  <span>{row.reference}</span>
                </Link>
              </strong>
              <div className="av-inline-metadata">
                <span>{row.issueLabel}</span>
                <StatusBadge label={row.statusLabel} tone={row.statusTone} />
              </div>
            </div>
          </td>
        );
      case 'customer':
        return (
          <td key={columnId} className="av-collection-table__cell">
            <div className="av-summary-stack">
              <strong>{row.customerName}</strong>
              <span>{row.dueLabel}</span>
            </div>
          </td>
        );
      case 'issuer':
        return (
          <td key={columnId} className="av-collection-table__cell">
            {row.issuerName}
          </td>
        );
      case 'issueDate':
        return (
          <td key={columnId} className="av-collection-table__cell">
            {row.issueLabel}
          </td>
        );
      case 'dueDate':
        return (
          <td key={columnId} className="av-collection-table__cell">
            {row.dueLabel}
          </td>
        );
      case 'amount':
        return (
          <td key={columnId} className="av-collection-table__cell">
            {row.totalLabel}
          </td>
        );
      case 'actions':
        return (
          <td key={columnId} className="av-collection-table__cell is-right">
            <OverflowMenuButton onClick={(event) => event.stopPropagation()} />
          </td>
        );
      default:
        return <td key={columnId} className="av-collection-table__cell" />;
    }
  }

  return (
    <div className="av-page">
      <SectionHeader
        title={t('invoices.title')}
        subtitle={`${t('invoices.summary.active', { count: summary.activeCount })} • ${t('invoices.summary.archived', {
          count: summary.archivedCount
        })}`}
      />

      <InlineQuickAddPanel domain="invoices" context={quickAddContext} onCreated={async () => setCollection(await listInvoices())} />

      <CollectionToolbar
        primary={
          <ToolbarGroup>
            {(['all', 'draft', 'issued', 'paid', 'overdue', 'archived'] as const).map((filter) => (
              <FilterChip
                key={filter}
                active={statusFilter === filter}
                label={t(`invoices.filters.${filter}`)}
                onClick={() => setStatusFilter(filter)}
              />
            ))}
          </ToolbarGroup>
        }
        secondary={
          <ToolbarGroup>
            <SelectField
              options={selectInvoiceCustomerOptions(collection, t)}
              value={customerFilter}
              onChange={(event) => setCustomerFilter(event.target.value)}
            />
            <SelectField
              options={selectInvoiceIssuerOptions(collection, t)}
              value={issuerFilter}
              onChange={(event) => setIssuerFilter(event.target.value)}
            />
            <SelectField
              options={[
                { value: 'all', label: t('invoices.filters.allDueStates') },
                { value: 'open', label: t('invoices.filters.open') },
                { value: 'overdue', label: t('invoices.filters.overdueState') },
                { value: 'paid', label: t('invoices.filters.paidState') }
              ]}
              value={dueFilter}
              onChange={(event) => setDueFilter(event.target.value as 'all' | 'open' | 'overdue' | 'paid')}
            />
            <SelectField
              options={[
                { value: 'issueDate', label: t('invoices.sort.issueDate') },
                { value: 'dueDate', label: t('invoices.sort.dueDate') },
                { value: 'number', label: t('invoices.sort.number') }
              ]}
              value={sort}
              onChange={(event) => handleSortSelectChange(event.target.value as InvoicesSort)}
            />
            <CollectionViewToggle
              options={[
                { value: 'list', label: t('common.view.list'), icon: 'list' },
                { value: 'grid', label: t('common.view.grid'), icon: 'grid' }
              ]}
              value={preferences.invoicesViewMode}
              onChange={(value) => void setViewMode(value)}
            />
            <IconButton
              icon="settings"
              label={t('common.columns.settingsButton')}
              onClick={() => setColumnSettingsOpen(true)}
            />
          </ToolbarGroup>
        }
      />

      {mutationError ? <div className="av-inline-error">{mutationError}</div> : null}

      <SplitWorkspaceLayout
        railMode="fixed"
        main={
          rows.length === 0 ? (
            <EmptyCollectionState title={t('invoices.title')} description={t('common.noResults')} />
          ) : preferences.invoicesViewMode === 'grid' ? (
            <CardGridSurface mode="grid">
              {rows.map((row) => {
                const selected = selectionSession.selectedIds.includes(row.id);

                return (
                  <PanelCard
                    key={row.id}
                    actions={
                      <OverflowMenuButton
                        onClick={(event) => {
                          event.stopPropagation();
                        }}
                      />
                    }
                    className={['av-collection-card', selected ? 'is-selected' : ''].filter(Boolean).join(' ')}
                  >
                    <div className="av-project-card__header">
                      <div className="av-project-card__identity">
                        <div className="av-project-card__icon-wrap">
                          <CollectionSelectionToggle
                            selected={selected}
                            className="av-card-selection-toggle"
                            icon="invoices"
                            label={row.reference}
                            data-testid="av-card-selection-toggle"
                            onClick={(event) => handleToggleSelection(row.id, event)}
                          />
                        </div>
                        <div className="av-collection-table__identity-copy">
                          <strong>
                            <Link className="av-inline-link" to={`/invoices/${row.id}`}>
                              <span>{row.reference}</span>
                            </Link>
                          </strong>
                          <div className="av-inline-metadata">
                            <span>{row.issueLabel}</span>
                            <StatusBadge label={row.statusLabel} tone={row.statusTone} />
                          </div>
                        </div>
                      </div>
                    </div>
                    <div className="av-project-card__footer">
                      <div className="av-summary-stack">
                        <strong>{row.customerName}</strong>
                        <span>{row.dueLabel}</span>
                      </div>
                      <div className="av-summary-stack">
                        <strong>{row.totalLabel}</strong>
                        <span>{row.issuerName}</span>
                      </div>
                    </div>
                  </PanelCard>
                );
              })}
            </CardGridSurface>
          ) : (
            <DataTableSurface>
              <CollectionTable
                ariaLabel={t('invoices.title')}
                columns={visibleInvoiceColumns}
                sortState={tableSortState}
                onSortChange={handleTableSortChange}
              >
                {rows.map((row) => {
                  const selected = selectionSession.selectedIds.includes(row.id);

                  return (
                    <tr
                      key={row.id}
                      className={['av-collection-table__row', selected ? 'is-selected' : ''].filter(Boolean).join(' ')}
                    >
                      {visibleInvoiceColumns.map((column) => renderInvoiceCell(row, column.id))}
                    </tr>
                  );
                })}
              </CollectionTable>
            </DataTableSurface>
          )
        }
        rail={
          selectedItems.length > 0 ? (
            <BulkEditorRail
              title={t('invoices.selection.title', { count: selectedItems.length })}
              hiddenSelectedCount={selectionSession.hiddenSelectedCount}
              items={selectedItems.map((item) => ({
                id: item.id,
                icon: 'invoices',
                label: item.label,
                subtitle: item.subtitle
              }))}
              onClose={() => {
                clearSelection();
                setInvoiceBulkDraft(createInitialInvoiceBulkDraft());
              }}
              onRemoveItem={(itemId) => removeSelectedId(itemId, visibleIds)}
              destructiveAction={{
                label: t('shell.delete'),
                onClick: () => void handleDeleteSelected(selectionSession.selectedIds)
              }}
              primaryAction={{
                label: t('common.applyChanges'),
                disabled: !hasInvoiceBulkDraftChanges || !isInvoiceBulkDraftValid,
                onClick: () => void handleApplyBulkChanges()
              }}
              secondaryAction={{
                label: t('common.cancel'),
                onClick: () => {
                  clearSelection();
                  setInvoiceBulkDraft(createInitialInvoiceBulkDraft());
                }
              }}
            >
              <div className="av-bulk-editor-form">
                <label className="av-form-field">
                  <span>{t('invoices.fields.status')}</span>
                  <SelectField
                    options={[
                      { value: 'unchanged', label: t('common.leaveUnchanged') },
                      ...invoiceStatuses.map((status) => ({
                        value: status,
                        label: t(`enums.invoiceStatus.${status}`)
                      }))
                    ]}
                    value={invoiceBulkDraft.status.mode === 'set' ? invoiceBulkDraft.status.value : 'unchanged'}
                    onChange={(event) =>
                      updateInvoiceBulkDraft({
                        status:
                          event.target.value === 'unchanged'
                            ? { mode: 'unchanged' }
                            : { mode: 'set', value: event.target.value as InvoiceStatus }
                      })
                    }
                  />
                </label>

                <label className="av-form-field">
                  <span>{t('invoices.fields.project')}</span>
                  <SelectField
                    options={[
                      { value: 'unchanged', label: t('common.leaveUnchanged') },
                      { value: 'clear', label: t('common.clearValue') },
                      { value: 'set', label: t('common.setValue') }
                    ]}
                    value={invoiceBulkDraft.projectId.mode}
                    onChange={(event) =>
                      handleNullablePatchModeChange(event.target.value as PatchMode, projectOptions[0]?.value ?? '', 'projectId')
                    }
                  />
                  {invoiceBulkDraft.projectId.mode === 'set' ? (
                    <SelectField
                      options={projectOptions}
                      value={invoiceBulkDraft.projectId.value}
                      onChange={(event) => updateInvoiceBulkDraft({ projectId: { mode: 'set', value: event.target.value } })}
                    />
                  ) : null}
                </label>

                {(['dueDate', 'paidAt'] as const).map((field) => (
                  <label key={field} className="av-form-field">
                    <span>{field === 'dueDate' ? t('invoices.fields.dueDate') : t('invoices.fields.paidAt')}</span>
                    <SelectField
                      options={[
                        { value: 'unchanged', label: t('common.leaveUnchanged') },
                        { value: 'clear', label: t('common.clearValue') },
                        { value: 'set', label: t('common.setValue') }
                      ]}
                      value={invoiceBulkDraft[field].mode}
                      onChange={(event) => handleNullablePatchModeChange(event.target.value as PatchMode, new Date().toISOString(), field)}
                    />
                    {invoiceBulkDraft[field].mode === 'set' ? (
                      <TextField
                        type="date"
                        value={formatDateInputValue(invoiceBulkDraft[field].value)}
                        onValueChange={(value) =>
                          updateInvoiceBulkDraft({
                            [field]: { mode: 'set', value: value ? toIsoDate(value) : '' }
                          })
                        }
                      />
                    ) : null}
                  </label>
                ))}

                <label className="av-form-field">
                  <span>{t('invoices.fields.paymentNotes')}</span>
                  <SelectField
                    options={[
                      { value: 'unchanged', label: t('common.leaveUnchanged') },
                      { value: 'clear', label: t('common.clearValue') },
                      { value: 'set', label: t('common.setValue') }
                    ]}
                    value={invoiceBulkDraft.paymentNotes.mode}
                    onChange={(event) => handleNullablePatchModeChange(event.target.value as PatchMode, '', 'paymentNotes')}
                  />
                  {invoiceBulkDraft.paymentNotes.mode === 'set' ? (
                    <TextField
                      value={invoiceBulkDraft.paymentNotes.value}
                      onValueChange={(value) => updateInvoiceBulkDraft({ paymentNotes: { mode: 'set', value } })}
                    />
                  ) : null}
                </label>
              </div>
            </BulkEditorRail>
          ) : null
        }
      />

      <CollectionColumnSettingsModal
        columns={invoiceColumns}
        domain="invoices"
        isOpen={columnSettingsOpen}
        strings={{
          title: t('common.columns.settingsTitle'),
          description: t('common.columns.settingsDescription'),
          reset: t('common.columns.settingsReset'),
          close: t('common.close'),
          locked: t('common.columns.settingsLocked')
        }}
        visibleColumnIds={preferences.collectionColumnVisibility.invoices}
        onClose={() => setColumnSettingsOpen(false)}
        onReset={handleResetVisibleColumns}
        onVisibleColumnIdsChange={handleVisibleColumnIdsChange}
      />
    </div>
  );
}
